import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Button, Form, Card } from "react-bootstrap";
import { toast } from "react-toastify";
import Layout from "../../layout/Layout";
import ImageInput from "../../Component/theme-management/ImageInput";
import BulkIconImport from "../../Component/theme-management/BulkIconImport";
import ImageWithFallback from "../../Component/theme-management/ImageWithFallback";
import FileInput from "../../Component/FileInput";
import StatusSwitch from "../../Component/theme-management/StatusSwitch";
import { iconsApi, themesApi, defaultIconsApi, isAuthError } from "../../helpers/tmClient";

/** Derive a display file name from an existing remote image URL. */
const fileNameFromUrl = (url) => {
  if (!url) return "";
  try {
    return decodeURIComponent(url.split("/").pop().split("?")[0]) || "";
  } catch {
    return "";
  }
};

/**
 * IconThumb — small preview for an icon row. Shows a newly-selected
 * File (via a temporary object URL) or an already-hosted image URL.
 */
const IconThumb = ({ file, url }) => {
  const [objUrl, setObjUrl] = useState(null);
  useEffect(() => {
    if (file) {
      const u = URL.createObjectURL(file);
      setObjUrl(u);
      return () => URL.revokeObjectURL(u);
    }
    setObjUrl(null);
    return undefined;
  }, [file]);
  const src = objUrl || url || null;
  if (!src) return null;
  return (
    <ImageWithFallback
      src={src}
      alt="icon preview"
      className="rounded-2 border"
      style={{ width: 40, height: 40, objectFit: "contain", background: "#fff" }}
    />
  );
};

/**
 * ThemeIconForm — create/edit an **Icon Set**.
 *
 * Section 1 (card): Icon Set Information — theme, pack thumbnail (shown
 * in the app), premium & status.
 * Section 2 (card): Icons — a dynamic list of individual icons, each
 * with a name, optional deep link and an image. Rows can be added and
 * removed before saving. Entered data is preserved on validation errors.
 */
const emptyIcon = (key) => ({
  key,
  _id: null,
  icon_name: "",
  deep_link_url: "",
  file: null,
  fileName: "",
  existingUrl: null,
});

const ThemeIconForm = ({ mode = "add", initial = null }) => {
  const navigate = useNavigate();
  const isEdit = mode === "edit";
  const keyRef = useRef(1);
  const nextKey = () => keyRef.current++;

  const [themes, setThemes] = useState([]);
  const [values, setValues] = useState({
    theme_id: initial?.theme_id?._id || initial?.theme_id || "",
    premium: initial?.premium ?? false,
    status: initial?.status ?? true,
  });
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [icons, setIcons] = useState(() => {
    if (initial?.icons?.length) {
      return initial.icons.map((ic) => ({
        key: nextKey(),
        _id: ic._id || null,
        icon_name: ic.icon_name || "",
        deep_link_url: ic.deep_link_url || "",
        file: null,
        fileName: fileNameFromUrl(ic.icon_image),
        existingUrl: ic.icon_image || null,
      }));
    }
    return [emptyIcon(nextKey())];
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await themesApi.options();
        if (isAuthError(res)) return navigate("/");
        setThemes(res.data.data || []);
      } catch {
        toast.error("Failed to load themes", { position: "bottom-right", autoClose: 1000 });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On ADD, prefill the icon rows from the seeded default icons (name +
  // deep link). They remain fully editable; the admin just uploads each
  // image (optional) and can add/remove custom rows.
  useEffect(() => {
    if (isEdit) return;
    (async () => {
      try {
        const res = await defaultIconsApi.list();
        if (isAuthError(res)) return;
        const defaults = res.data.data || [];
        if (defaults.length) {
          setIcons(
            defaults.map((d) => ({
              key: nextKey(),
              _id: null,
              icon_name: d.icon_name || "",
              deep_link_url: d.deep_link_url || "",
              file: null,
              fileName: "",
              existingUrl: null,
            }))
          );
        }
      } catch {
        /* non-fatal — admin can still add icons manually */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------------------- set-level fields ------------------------ */
  const onChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((er) => ({ ...er, [name]: null }));
  };
  const onSwitch = (name) => (checked) => setValues((v) => ({ ...v, [name]: checked }));
  const onThumb = (file) => {
    setThumbnailFile(file);
    setErrors((er) => ({ ...er, thumbnail_image: null }));
  };

  /* ----------------------------- icon rows --------------------------- */
  const addIcon = () => setIcons((list) => [...list, emptyIcon(nextKey())]);

  const removeIcon = (key) =>
    setIcons((list) => (list.length > 1 ? list.filter((x) => x.key !== key) : list));

  const setIconField = (key, field, value) =>
    setIcons((list) => list.map((x) => (x.key === key ? { ...x, [field]: value } : x)));

  /**
   * Merge ZIP-imported icons into the editable list.
   *
   * For each imported icon:
   *   - If an existing row has the same name (case-insensitive), update only
   *     its image — the row's name, deep link, and position are preserved.
   *   - Otherwise append it as a new row.
   *
   * All existing rows that have no match are left completely untouched,
   * so pre-filled default icons with null images stay in the list as-is.
   */
  const handleImported = (importedIcons) => {
    if (!importedIcons || !importedIcons.length) return;
    setIcons((list) => {
      const updated = list.map((x) => ({ ...x }));
      const unmatched = [];

      for (const ic of importedIcons) {
        const nameLower = (ic.icon_name || "").trim().toLowerCase();
        const idx = updated.findIndex(
          (x) => x.icon_name.trim().toLowerCase() === nameLower
        );
        if (idx !== -1) {
          updated[idx] = {
            ...updated[idx],
            file: null,
            fileName: fileNameFromUrl(ic.icon_image),
            existingUrl: ic.icon_image || null,
          };
        } else {
          unmatched.push({
            key: nextKey(),
            _id: null,
            icon_name: ic.icon_name || "",
            deep_link_url: ic.deep_link_url || "",
            file: null,
            fileName: fileNameFromUrl(ic.icon_image),
            existingUrl: ic.icon_image || null,
          });
        }
      }

      return [...updated, ...unmatched];
    });
    setErrors((er) => ({ ...er, icons: null }));
  };

  // Standard file input (matches the Keyboard Theme "Thumb Image" field).
  const onIconFile = (key, i) => (e) => {
    const file = (e.target.files && e.target.files[0]) || null;
    setIcons((list) =>
      list.map((x) => (x.key === key ? { ...x, file, fileName: file ? file.name : "" } : x))
    );
    clearIconError(i, "icon_image");
  };

  const clearIconError = (idx, field) =>
    setErrors((er) => ({ ...er, [`icons.${idx}.${field}`]: null, icons: null }));

  /* ----------------------------- validate ---------------------------- */
  const validate = () => {
    const e = {};
    // Theme is optional — empty = Global Icon Set.
    if (!isEdit && !thumbnailFile) e.thumbnail_image = "Thumbnail is required";
    if (isEdit && !thumbnailFile && !initial?.thumbnail_image)
      e.thumbnail_image = "Thumbnail is required";
    if (!icons.length) e.icons = "Add at least one icon";
    icons.forEach((ic, i) => {
      if (!ic.icon_name.trim()) e[`icons.${i}.icon_name`] = "Icon name is required";
      // Icon image is optional — it can be added later.
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ------------------------------ submit ----------------------------- */
  const submit = async () => {
    if (!validate()) {
      toast.error("Please fix the highlighted fields", { position: "bottom-right", autoClose: 1200 });
      return;
    }

    const fd = new FormData();
    fd.append("theme_id", values.theme_id);
    fd.append("premium", values.premium);
    fd.append("status", values.status);
    if (thumbnailFile) fd.append("thumbnail_image", thumbnailFile);

    // Build the icons metadata + attach each new image under its field.
    const meta = icons.map((ic, i) => {
      const entry = {
        icon_name: ic.icon_name.trim(),
        deep_link_url: ic.deep_link_url.trim() || null,
      };
      if (ic._id) entry._id = ic._id;
      if (ic.file) {
        const field = `icon_image_${i}`;
        entry.image_field = field;
        fd.append(field, ic.file);
      } else if (ic.existingUrl) {
        // Pre-hosted image (bulk-imported, or kept from a prior save):
        // pass the URL so the backend persists it for new icons.
        entry.icon_image = ic.existingUrl;
      }
      return entry;
    });
    fd.append("icons", JSON.stringify(meta));

    setSaving(true);
    try {
      const res = isEdit ? await iconsApi.update(initial._id, fd) : await iconsApi.create(fd);
      if (res.data.status) {
        toast.success(res.data.response_message || "Saved", { position: "bottom-right", autoClose: 700 });
        navigate("/theme-icons");
      } else if (res.data.errors) {
        setErrors(res.data.errors);
        toast.error(res.data.response_message || "Validation failed", { position: "bottom-right", autoClose: 1200 });
      } else {
        toast.error(res.data.response_message || "Something went wrong", { position: "bottom-right", autoClose: 1200 });
      }
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors) setErrors(data.errors);
      toast.error(data?.response_message || "Request failed", { position: "bottom-right", autoClose: 1200 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout sidebar={true}>
      <div className="page-heading">
        <h3 className="my-1">{isEdit ? "Edit Icon Set" : "Add Icon Set"}</h3>
      </div>
      <div className="page-content">
        <Form>
          {/* ------------------ Section 1: Icon Set Info ------------------ */}
          <Card className="mb-4">
            <Card.Header><strong>Icon Set Information</strong></Card.Header>
            <Card.Body>
              {saving && <div className="loader table-loader"></div>}
              <fieldset disabled={saving} style={{ border: "none", padding: 0, margin: 0 }}>
              <Row>
                <Col lg={4} sm={12}>
                  <Form.Group>
                    <Form.Label>Theme</Form.Label>
                    <Form.Select name="theme_id" className="my-2" value={values.theme_id} onChange={onChange}>
                      <option value="">Select Theme (Optional)</option>
                      {themes.map((t) => (
                        <option key={t._id} value={t._id}>{t.name} ({t.theme_type})</option>
                      ))}
                    </Form.Select>
                    <Form.Text muted>Leave empty to create a Global Icon Set.</Form.Text>
                  </Form.Group>

                  <Row className="mt-2">
                    <Col xs={6}>
                      <Form.Label className="d-block">Premium</Form.Label>
                      <StatusSwitch checked={values.premium} disabled={saving} onChange={onSwitch("premium")} onColor="#8b5cf6" onLabel="ON" offLabel="OFF" />
                    </Col>
                    <Col xs={6}>
                      <Form.Label className="d-block">Active</Form.Label>
                      <StatusSwitch checked={values.status} disabled={saving} onChange={onSwitch("status")} />
                    </Col>
                  </Row>
                </Col>

                <Col lg={4} sm={12}>
                  <ImageInput
                    label="Thumbnail Image (pack preview)"
                    required={!isEdit}
                    existingUrl={initial?.thumbnail_image}
                    onChange={onThumb}
                    error={errors.thumbnail_image}
                    disabled={saving}
                  />
                  <Form.Text muted>Shown in the app as the cover of the icon pack — not an individual icon.</Form.Text>
                </Col>

                <Col lg={4} sm={12}>
                  <Form.Label className="fw-semibold">Bulk Import Icons</Form.Label>
                  <p className="text-muted small mb-2">
                    Upload a <strong>.zip</strong> of PNG icons. Names are derived from file names
                    (e.g. <code>adobe_scan.png</code> → <em>Adobe Scan</em>). Imported icons are
                    added to the list below for review — nothing is saved until you click <strong>Save</strong>.
                  </p>
                  <BulkIconImport onImported={handleImported} disabled={saving} />
                </Col>
              </Row>
              </fieldset>
            </Card.Body>
          </Card>

          {/* --------------------- Section 2: Icons ---------------------- */}
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Icons</strong>
            </Card.Header>
            <Card.Body>
              {errors.icons && <div className="text-danger small mb-2">{errors.icons}</div>}
              <fieldset disabled={saving} style={{ border: "none", padding: 0, margin: 0 }}>
              {icons.map((ic, i) => (
                <div key={ic.key} className="border rounded p-3 mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Icon #{i + 1}</strong>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="btn-icon"
                      title="Remove icon"
                      disabled={icons.length === 1 || saving}
                      onClick={() => removeIcon(ic.key)}
                    >
                      <i className="bx bx-trash-alt"></i>
                    </Button>
                  </div>
                  <Row>
                    <Col lg={4} sm={12}>
                      <Form.Group>
                        <Form.Label>Icon Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          className="my-2"
                          placeholder="e.g. Instagram"
                          value={ic.icon_name}
                          isInvalid={!!errors[`icons.${i}.icon_name`]}
                          onChange={(e) => { setIconField(ic.key, "icon_name", e.target.value); clearIconError(i, "icon_name"); }}
                        />
                        <Form.Control.Feedback type="invalid">{errors[`icons.${i}.icon_name`]}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col lg={4} sm={12}>
                      <Form.Group>
                        <Form.Label>Deep Link URL</Form.Label>
                        <Form.Control
                          type="text"
                          className="my-2"
                          placeholder="e.g. instagram://"
                          value={ic.deep_link_url}
                          onChange={(e) => setIconField(ic.key, "deep_link_url", e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col lg={4} sm={12}>
                      <div className="d-flex align-items-end gap-2">
                        <div className="flex-grow-1">
                          <FileInput
                            label="Icon Image"
                            name={`icon_image_${ic.key}`}
                            accept="image/*"
                            fileName={ic.fileName}
                            onChange={onIconFile(ic.key, i)}
                            isInvalid={!!errors[`icons.${i}.icon_image`]}
                            feedback={errors[`icons.${i}.icon_image`]}
                          />
                        </div>
                        {(ic.file || ic.existingUrl) && (
                          <div className="mb-1">
                            <IconThumb file={ic.file} url={ic.existingUrl} />
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              ))}

              <Button variant="outline-primary" size="sm" onClick={addIcon}>
                <i className="bx bx-plus"></i> Add Icon
              </Button>
              </fieldset>
            </Card.Body>
            <Card.Footer className="text-end">
              <Button variant="primary" className="me-3 d-inline-flex align-items-center gap-2" onClick={submit} disabled={saving}>
                {saving && <span className="spinner-border spinner-border-sm" role="status"></span>}
                {saving ? "Saving..." : "Save"}
              </Button>
              <Link to="/theme-icons"><Button variant="secondary" disabled={saving}>Cancel</Button></Link>
            </Card.Footer>
          </Card>
        </Form>
      </div>
    </Layout>
  );
};

export default ThemeIconForm;
